import { createSignal, createEffect, onCleanup } from "solid-js";
import {
  useSyncClient,
  useCallOptions,
  withBearerToken,
  SyncService,
  createSyncResourceQuery,
  dateToTimestampOptional,
} from "@storytell/cp-client";
import { useWire } from "~/wire";
import type { AssetRecord } from "~/stores/sync/syncAssets";
import type { SyncMeta } from "~/stores/sync/syncTypes";

export type SyncStatus = "idle" | "syncing" | "success" | "error";

export interface SyncState {
  status: () => SyncStatus;
  error: () => string | null;
}

export interface SyncActions {
  performSync: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export interface SyncOptions {
  enablePeriodicSync?: boolean;
  syncIntervalMs?: number;
  limit?: number;
  autoStartOnAuth?: boolean;
}

const DEFAULT_OPTIONS: Required<SyncOptions> = {
  enablePeriodicSync: false,
  syncIntervalMs: 30000, // 30 seconds
  limit: 50,
  autoStartOnAuth: true,
};

/**
 * Hook for managing asset synchronization with the backend.
 *
 * Provides incremental sync functionality with proper authentication,
 * state management, and error handling.
 *
 * @param options Configuration options for sync behavior
 * @returns Object containing sync state and actions
 */
export function useSync(options: SyncOptions = {}): [SyncState, SyncActions] {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const wire = useWire();

  const sync = wire.sync;

  const lastSyncTime = (entity: { getMeta: () => SyncMeta }) => entity.getMeta().nextAfter;
  const lastSeenId = (entity: { getMeta: () => SyncMeta }) => entity.getMeta().lastSeenId;
  const lastSeenVersion = (entity: { getMeta: () => SyncMeta }) => entity.getMeta().lastSeenVersion;

  // State management
  const [status, setStatus] = createSignal<SyncStatus>("idle");
  const [error, setError] = createSignal<string | null>(null);

  // Get sync client
  const syncClient = useSyncClient();

  // Main sync function
  const performSync = async (): Promise<void> => {
    if (!wire.auth.isReady()) {
      console.log("Auth not ready, skipping sync");
      return;
    }

    setStatus("syncing");
    setError(null);
    console.log("Starting incremental sync for assets and concepts...");

    try {
      // Create incremental request for assets
      const assetQuery = createSyncResourceQuery({
        resource: SyncService.SyncResource.ASSET,
        after: dateToTimestampOptional(lastSyncTime(sync.assets) || undefined),
        limit: opts.limit,
        ...(lastSeenId(sync.assets) && { lastSeenId: lastSeenId(sync.assets)! }),
        ...(lastSeenVersion(sync.assets) && { lastSeenVersion: lastSeenVersion(sync.assets)! }),
      });

      // Create incremental request for concepts
      const conceptQuery = createSyncResourceQuery({
        resource: SyncService.SyncResource.CONCEPT,
        after: dateToTimestampOptional(lastSyncTime(sync.concepts) || undefined),
        limit: opts.limit,
        ...(lastSeenId(sync.concepts) && { lastSeenId: lastSeenId(sync.concepts)! }),
        ...(lastSeenVersion(sync.concepts) && { lastSeenVersion: lastSeenVersion(sync.concepts)! }),
      });

      const request = {
        queries: [assetQuery, conceptQuery],
      };

      console.log("Sync request:", request);

      // Get bearer token and create call options
      const token = wire.auth.token();
      if (!token) {
        const errorMsg = "No auth token available for sync request";
        console.error(errorMsg);
        setError(errorMsg);
        setStatus("error");
        return;
      }

      const callOptions = useCallOptions(withBearerToken(token));

      // Make the sync request
      const result = await syncClient.incremental(request, callOptions);

      if (result.isOk()) {
        const response = result.value;

        // Process the response
        if (response.records && response.records.length > 0) {
          // Process each record set in the response
          for (const recordSet of response.records) {
            if (recordSet.resource === SyncService.SyncResource.ASSET) {
              // Update synced assets (append new ones)
              if (recordSet.records) {
                const records: AssetRecord[] = [];
                for (const record of recordSet.records) {
                  if (record.record.case === "asset") {
                    records.push({
                      id: record.record.value.data.id,
                      ...record.record.value,
                      userId: wire.auth.user()?.id || "",
                    });
                  }
                }
                sync.assets.addBulk(records);
              }
            } else if (recordSet.resource === SyncService.SyncResource.CONCEPT) {
              // SyncResource.CONCEPT
              // Process concept records
              if (recordSet.records) {
                const conceptRecords = [];
                for (const record of recordSet.records) {
                  if (record.record.case === "concept") {
                    conceptRecords.push({
                      ...record.record.value,
                      userId: wire.auth.user()?.id || "",
                    });
                  }
                }
                console.log("Received concepts:", conceptRecords);
                sync.concepts.addBulk(conceptRecords);
              }
            }

            // Handle shared actions for setting the metadata
            const actions = recordSet.resource === 1 ? sync.assets : sync.concepts;

            // Update last sync time and lastSeenId for next incremental sync
            // Convert protobuf timestamp to JavaScript Date with full precision
            if (recordSet.nextAfter) {
              const timestampMs =
                Number(recordSet.nextAfter.seconds) * 1000 + Math.floor((recordSet.nextAfter.nanos || 0) / 1000000);
              let nextSyncTime = new Date(timestampMs);
              // Always advance the cursor, even if the backend returns the same timestamp as before.
              // If the backend returns the same timestamp, increment by 1ms to ensure forward progress and avoid fetching the same record repeatedly.
              if (lastSyncTime(actions) && nextSyncTime.getTime() <= lastSyncTime(actions)!.getTime()) {
                nextSyncTime = new Date(lastSyncTime(actions)!.getTime() + 1);
              }
              actions.setMeta((meta) => ({
                ...meta,
                nextAfter: nextSyncTime,
              }));
            }

            // Update lastSeenId and lastSeenVersion for compound cursor pagination
            if (recordSet.lastSeenId) {
              actions.setMeta((meta) => ({
                ...meta,
                lastSeenId: recordSet.lastSeenId,
              }));
            } else {
              // If no lastSeenId, clear it
              actions.setMeta((meta) => ({
                ...meta,
                lastSeenId: null,
              }));
            }

            if (recordSet.lastSeenVersion) {
              actions.setMeta((meta) => ({
                ...meta,
                lastSeenVersion: recordSet.lastSeenVersion,
              }));
            } else {
              // If no lastSeenVersion, clear it
              actions.setMeta((meta) => ({
                ...meta,
                lastSeenVersion: null,
              }));
            }
          }
        }

        setStatus("success");
      } else {
        const errorMsg = `Sync failed: ${result.error.message}`;
        console.error(errorMsg, result.error);
        setError(errorMsg);
        setStatus("error");
      }
    } catch (error) {
      const errorMsg = `Sync error: ${error}`;
      console.error(errorMsg, error);
      setError(errorMsg);
      setStatus("error");
    }
  };

  // Actions
  const clearError = () => setError(null);

  const reset = () => {
    setStatus("idle");
    sync.assets.setMeta((meta) => ({
      ...meta,
      nextAfter: null,
      lastSeenId: null,
      lastSeenVersion: null,
    }));
    // setSyncedAssets([]);
    sync.assets.clear();
    setError(null);
  };

  // Auto-start sync when auth is ready (if enabled)
  if (opts.autoStartOnAuth) {
    createEffect(() => {
      if (wire.auth.isReady() && status() === "idle") {
        performSync();
      }
    });
  }

  // Periodic sync (if enabled)
  if (opts.enablePeriodicSync) {
    createEffect(() => {
      if (status() === "success" || status() === "error") {
        const interval = setInterval(() => {
          if (wire.auth.isReady()) {
            performSync();
          }
        }, opts.syncIntervalMs);

        // Cleanup interval on component unmount
        onCleanup(() => clearInterval(interval));
      }
    });
  }

  // Return state and actions
  return [
    {
      status,
      error,
    },
    {
      performSync,
      clearError,
      reset,
    },
  ];
}
